<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted } from 'vue'
import { useAppMode } from '@/composables/useAppMode'
import UiButton from '@/components/ui/button/Button.vue'
import DotsLoader from '@/components/DotsLoader.vue'
import ResetSvg from '@/assets/icons/reset.svg'
import DrillActionTile from './DrillActionTile.vue'
import DrillActionTilePlayer from './DrillActionTilePlayer.vue'
import DrillActionTileCommunity from './DrillActionTileCommunity.vue'

import ArrowLeft from '@/assets/icons/outline-arrow-left.svg'
import ArrowRight from '@/assets/icons/outline-arrow-right.svg'

import { useDebounceFn } from '@vueuse/core'

import type { DrillAtom, DrillAtomRound } from '@acepoker/contract'

const tiles = defineModel<DrillAtom[]>({ default: [] })

const props = defineProps<{
  loading?: boolean
  nextTile: DrillAtom
}>()

const actionListRef = ref()
const lastActiveTile = ref<null | number>(null)

watch(
  () => tiles.value.length,
  () => {
    nextTick(() => {
      scrollToLastAction()
      onScroll()
    })
  }
)

function updateListAction(i = 0, data: DrillAtomRound) {
  tiles.value.splice(i, tiles.value.length - i, data)
  lastActiveTile.value = i
}
function pushNewAction(data: DrillAtomRound) {
  tiles.value.push(data)
  lastActiveTile.value = tiles.value.length - 1
}

function handleReset() {
  tiles.value.splice(0, tiles.value.length)
  lastActiveTile.value = null
}

function scrollToLastAction() {
  actionListRef.value.scrollTo({
    left: actionListRef.value.scrollWidth,
    behavior: 'smooth'
  })
}
function scrollToStart() {
  actionListRef.value.scrollTo({
    left: 0,
    behavior: 'smooth'
  })
}

const atStart = ref(true)
const atEnd = ref(true)
const onScroll = useDebounceFn(() => {
  atStart.value = actionListRef.value.scrollLeft === 0 ? true : false

  const scrollAccuracy = 10
  atEnd.value =
    actionListRef.value.scrollLeft + actionListRef.value.clientWidth + scrollAccuracy >
      actionListRef.value.scrollWidth ||
    actionListRef.value.scrollWidth <= actionListRef.value.clientWidth
      ? true
      : false
}, 500)

onMounted(() => {
  onScroll()
})

const allCommunityActions = computed(() => tiles.value.filter((el) => el.kind === 'round'))
</script>

<template>
  <section class="-mx-4">
    <div class="relative">
      <button
        v-if="!atStart"
        class="absolute top-0 left-0 z-10 h-full w-[40px] cursor-pointer bg-linear-to-l from-transparent to-[#356498] p-0 text-white hover:to-[#275b97] hover:text-white/80"
        @click="scrollToStart"
      >
        <ArrowLeft />
      </button>
      <button
        v-if="!atEnd"
        class="absolute top-0 right-0 z-10 h-full w-[40px] cursor-pointer bg-linear-to-r from-transparent to-[#356498] p-0 text-white hover:to-[#275b97] hover:text-white/80"
        @click="scrollToLastAction"
      >
        <ArrowRight />
      </button>

      <div
        class="tiles-list relative flex gap-1 overflow-x-auto px-4"
        ref="actionListRef"
        @scroll="onScroll"
      >
        <template v-for="(el, i) in tiles">
          <DrillActionTilePlayer
            v-if="el.kind === 'action'"
            :key="`action_${i}`"
            :data="el"
            :active="lastActiveTile === i"
            @change="(tile) => updateListAction(i, tile)"
          />
          <DrillActionTileCommunity
            v-else-if="el.kind === 'round'"
            :key="`round_${i}`"
            :data="el"
            :active="lastActiveTile === i"
            :allCommunityActions="allCommunityActions"
            @change="(tile) => updateListAction(i, tile)"
          />
        </template>

        <!-- new action tile -->
        <Transition name="new-tile">
          <DrillActionTile v-if="loading" class="pending-tile">
            <div class="flex h-full items-center justify-center">
              <dots-loader size="large" />
            </div>
          </DrillActionTile>
          <template v-else-if="nextTile">
            <DrillActionTilePlayer
              v-if="nextTile.kind === 'action'"
              :data="nextTile"
              @change="(tile) => pushNewAction(tile)"
            />
            <DrillActionTileCommunity
              v-else-if="nextTile.kind === 'round'"
              :data="nextTile"
              :allCommunityActions="allCommunityActions"
              @change="(tile) => pushNewAction(tile)"
            />
            <DrillActionTile v-else-if="nextTile.kind === 'end'">
              <div
                class="flex h-full min-h-12 items-center justify-center rounded-sm border border-white/8"
              >
                GG
              </div>
            </DrillActionTile>
          </template>
        </Transition>
      </div>
    </div>

    <div class="mt-6 flex justify-center">
      <UiButton
        variant="secondary2"
        class="h-[30px] px-2.5 py-1 text-xs font-normal"
        @click="handleReset"
      >
        <ResetSvg class="mr-1 w-[18px]" />
        Reset
      </UiButton>
    </div>
  </section>
</template>

<style scoped>
.tiles-list::-webkit-scrollbar {
  display: none;
}

/* new tile anumation */
.new-tile-enter-active {
  transition: all 0.5s ease-out;
}
.new-tile-enter-from {
  opacity: 0;
}
</style>
